/**
 * Combined Report page composition (Combined Report.png order): top bar →
 * property strip (Combined active) → family header → AI summary → tiles →
 * applicant/guarantor overviews → household verification → combined flags
 * → individual flags → per-member Additional Documents → per-member
 * Applicant Consent (the last two are accordions over the individual
 * page's own section bodies, client 2026-09-08). Decision actions gate on
 * the GROUP status through
 * getLandlordGroupActions — terminal households show the status chip and
 * no buttons (DETAIL-V2-DECISION-RULES.md), exactly like the individual
 * page. Family (re)generate ships here (kebab item + failed/empty-card CTA →
 * FamilyRegenerateDialog); the header's Timeline button opens the shared
 * multi-applicant TimelinePanel (QA 2026-07-15 — follow-up shipped).
 */
import { Check, ClockFading, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router'
import {
	demoHidesIndividualFlags,
	isDemoProperty,
} from '#app/components/application-detail-v2/demo/demo-hooks'
import {
	countEligibleCoResidents,
	countEligibleGuarantors,
	deriveCombinedReportView,
	deriveFamilyReportAction,
	deriveHouseholdFullyDecided,
} from '#app/utils/application-detail-v2/combined-display'
import type { CombinedReportData } from '#app/utils/application-detail-v2/combined-types'
import {
	preserveReturnTo,
	resolveBackTarget,
} from '#app/utils/application-detail-v2/return-to'
import type {
	GroupMember,
	HouseholdChild,
} from '#app/utils/application-detail-v2/types'
import {
	getLandlordGroupActions,
	isDecisionLocked,
	toApplicationStatusForLandlordActions,
} from '#app/utils/landlord-application-actions'
import { cn } from '#app/utils/misc'
import { KebabMenu } from '../HeaderMenus'
import { PropertyStrip } from '../PropertyStrip'
import { SummaryTilesView } from '../SummaryTiles'
import type { DownloadsApi } from '../downloads/useDownloads'
import { DETAIL_CONTAINER } from '../primitives'
import { AiSummaryBody } from '../sections/AiSummary'
import { RiskFlagPanels } from '../sections/RiskFlags'
import { CombinedDownloadMenu } from './CombinedDownloadMenu'
import { CombinedHeader } from './CombinedHeader'
import { CombinedVerificationSummary } from './CombinedVerificationSummary'
import { FamilyProcessingSection } from './FamilyProcessingSection'
import { IndividualFlags } from './IndividualFlags'
import { MemberConsents } from './MemberConsents'
import { MemberDocuments } from './MemberDocuments'
import { MemberOverviews } from './MemberOverviews'

export interface CombinedPageHandlers {
	onApprove?: () => void
	onDecline?: () => void
	onWithdraw?: () => void
	onReinstate?: () => void
	onSendUpdateLink?: () => void
	onAddApplicant?: () => void
	onRequestGuarantor?: () => void
	/** Household-wide requests + contact, mirroring the individual page's
	 * kebab (client 2026-08-27). */
	onRequestBankAccount?: () => void
	onRequestDocuments?: () => void
	onReachOut?: () => void
	onDeleteApplication?: () => void
	/** Mixed-household upsell — upgrades every pre-screening member. */
	onProceedToFullScreen?: () => void
	onOpenTopup?: () => void
	onRemoveMember?: (member: GroupMember) => void
	/** Opens the Remove Child confirm for a household child tile (client
	 * 2026-09-07 — reverses the 2026-08-27 "no child ✕" rule). */
	onRemoveChild?: (child: HouseholdChild) => void
	/** Opens the family (re)generate dialog (kebab item). */
	onRegenerateReport?: () => void
	/** Opens the shared multi-applicant TimelinePanel (QA 2026-07-15). */
	onOpenTimeline?: () => void
}

const formatMoney = (value: number) =>
	`$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`

export function CombinedReportPage({
	data,
	handlers = {},
	downloadsApi,
	profileMenu,
}: {
	data: CombinedReportData
	handlers?: CombinedPageHandlers
	downloadsApi?: DownloadsApi
	/** Account avatar + menu for the top bar (the v2 route hides the shared
	 * SiteHeader) — individual-page parity, Figma 2026-09-07. */
	profileMenu?: ReactNode
}) {
	const navigate = useNavigate()
	const location = useLocation()
	// Same origin-aware back target the individual header uses (return-to.ts).
	const searchParams = new URLSearchParams(location.search)
	const { application, can, familyPredictions, billing } = data
	const propertyId = application.propertyId ?? ''

	const actions = getLandlordGroupActions(
		toApplicationStatusForLandlordActions(data.groupStatus),
	)
	// An accepted household keeps Withdraw (rule 1 carve-out) but stays
	// LOCKED for requests/regenerate — derive from the status, not the map.
	const decisionLocked = isDecisionLocked(data.groupStatus)
	// Hide an action every member already carries (client 2026-07-21): a
	// fully-accepted household offers no Accept, a fully-declined one no
	// Decline. Mixed / partly-undecided households keep both — the console is
	// still the entry point for the remaining members.
	const { allAccepted, allDeclined } = deriveHouseholdFullyDecided(data.members)
	const showAccept = actions.accept && can.approve && !allAccepted
	const showDecline = actions.decline && can.decline && !allDeclined
	// Family (re)generate offering (COMBINED-V2-DATA-MAP §3): 'generate' for a
	// failed/missing family report, 'regenerate' for a finished one. Gated on
	// permission + undecided household + composition ready (standard ≥2
	// co-residents; student ≥1 applicant + ≥1 completed guarantor).
	const isStudentHousing = data.applicationCategory === 'student_housing'
	const familyReportAction = deriveFamilyReportAction({
		canRegenerate: data.canRegenerate,
		decisionLocked,
		familyStatus: data.familyScore?.processingStatus ?? null,
		eligibleCoResidentCount: countEligibleCoResidents(data.members),
		eligibleGuarantorCount: countEligibleGuarantors(data.members),
		isStudentHousing,
		preScreenApplicantCount: data.preScreenApplicants.length,
		allApplicantsGuarantorLinked: data.studentGuarantorLinksReady,
	})
	// Which family-report body to show (2026-07-17): the page used to render
	// the summary/tiles with NULLS while a run was in flight — blank cards,
	// no progress, no polling. Same three-way split the individual page uses.
	const reportView = deriveCombinedReportView({
		familyStatus: data.familyScore?.processingStatus ?? null,
		hasReport: familyPredictions != null,
	})
	const kebabItems = {
		addApplicant: can.addApplicant,
		// The Guarantor's Overview header used to host this action; that
		// section is gone (2026-08-27), so the kebab is now its home.
		requestGuarantor: can.requestGuarantor,
		// Both requests pick their targets in-dialog, so the household view can
		// offer them exactly like the individual page (client 2026-08-27).
		requestBankAccount: !decisionLocked && can.requestBankAccount,
		requestDocuments: !decisionLocked && can.requestDocuments,
		editProperty: data.canEditProperty && Boolean(propertyId),
		reachOut: true,
		sendUpdateLink: can.sendUpdateLink,
		withdraw: actions.withdraw && can.withdraw,
		reinstate: data.groupStatus === 'withdrawn' && can.reinstate,
		reportAction: familyReportAction,
		// Deletes the ENTRY application (the one this household view was opened
		// from) — the dialog names them, so it can't be mistaken for a
		// household-wide delete (client 2026-08-27).
		deleteApplication: can.deleteApplication,
	}
	const showKebab = Object.values(kebabItems).some(Boolean)

	const rentRange = familyPredictions?.rentRange
		? `${formatMoney(familyPredictions.rentRange.min)} – ${formatMoney(
				familyPredictions.rentRange.max,
			)} /mo`
		: null
	const savings =
		familyPredictions?.savingsInvestments != null
			? formatMoney(familyPredictions.savingsInvestments)
			: null

	return (
		<div className="min-h-screen bg-[#FBFDFD] pb-16">
			<header>
				<div className="sticky top-0 z-40 bg-[#FBFDFD] shadow-[0px_4px_4px_0px_#0000000D]">
					<div
						className={cn(
							DETAIL_CONTAINER,
							'flex flex-wrap items-center justify-between gap-3 py-3',
						)}
					>
						<div className="flex min-w-0 items-center gap-3">
							{/* Same top bar as the individual report (client 2026-08-27):
							    the round arrow is the only control and "Report" is a page
							    title, not a link. */}
							<button
								type="button"
								aria-label="Back to applications"
								className="flex shrink-0 cursor-pointer items-center justify-center rounded-full transition-opacity hover:opacity-70"
								onClick={() => {
									// Origin-aware back (client 2026-07-24): the remembered
									// list URL, else All Applications — replaces the old
									// location.key/history.back heuristic (return-to.ts).
									void navigate(resolveBackTarget(searchParams))
								}}
							>
								{/* The glyph carries its own circle — no border here. */}
								<img
									src="/img/circular-back-icon.svg"
									alt=""
									className="size-6 shrink-0"
								/>
							</button>
							{/* Figma (2026-09-07): the bar carries ONLY the title — no
							    "Combined Report" label and no household status chip; the
							    header card below already names the household. */}
							<span className="font-calgary shrink-0 text-[20px] leading-tight font-medium text-[#1F1E1D] lg:text-[30px]">
								Report
							</span>
						</div>
						<div className="flex flex-wrap items-center gap-3">
							{billing ? (
								handlers.onOpenTopup ? (
									<button
										type="button"
										onClick={handlers.onOpenTopup}
										className="inline-flex h-11 cursor-pointer items-center gap-2 rounded-full border border-[#ECECEA] bg-white px-4 text-sm text-[#32302F] transition-colors hover:bg-[#F6F7F5]"
									>
										<img src="/img/coin.svg" alt="" />
										<span className="font-semibold">{billing.balance}</span>
										<span className="text-[#747474]">Credits</span>
									</button>
								) : (
									<span className="inline-flex h-11 items-center gap-2 rounded-full border border-[#ECECEA] bg-white px-4 text-sm text-[#32302F]">
										<img src="/img/coin.svg" alt="" />
										<span className="font-semibold">{billing.balance}</span>
										<span className="text-[#747474]">Credits</span>
									</span>
								)
							) : null}
							{/* Figma order (2026-09-07): Timeline BEFORE Download. */}
							<button
								type="button"
								onClick={handlers.onOpenTimeline}
								className="flex h-11 cursor-pointer items-center gap-2 rounded-full border border-[#ECECEA] bg-white px-4 text-sm font-medium text-[#32302F] transition-colors hover:bg-[#F6F7F5]"
							>
								<ClockFading className="size-4 text-[#747474]" />
								Timeline
							</button>
							{can.download && downloadsApi ? (
								<CombinedDownloadMenu
									downloads={data.downloads}
									api={downloadsApi}
								/>
							) : null}
							{showAccept ? (
								<button
									type="button"
									onClick={handlers.onApprove}
									className="flex h-11 cursor-pointer items-center gap-2 rounded-full bg-green-50 px-5 text-sm font-semibold text-green-600 transition-colors hover:bg-green-100"
								>
									<Check className="size-4" />
									Accept
								</button>
							) : null}
							{showDecline ? (
								<button
									type="button"
									onClick={handlers.onDecline}
									className="flex h-11 cursor-pointer items-center gap-2 rounded-full bg-red-50 px-5 text-sm font-semibold text-red-500 transition-colors hover:bg-red-100"
								>
									<X className="size-4" />
									Decline
								</button>
							) : null}
							{/* Account menu — same slot as the individual top bar. */}
							{profileMenu}
						</div>
					</div>
				</div>

				<PropertyStrip
					property={data.property}
					groupMembers={data.members}
					propertyId={propertyId}
					currentName={application.applicantName}
					currentApplicationId={application.id}
					// Live invites in the switcher — individual-page parity
					// (2026-07-17); PendingInvitee is a superset of the summary shape.
					pendingInvitations={data.pendingInvitees}
					screeningKind="full"
					view="combined"
					// Kebab lives in the strip on BOTH report pages (client
					// 2026-08-27) — it used to sit in the combined top bar while
					// the individual page kept it here.
					actions={
						showKebab ? (
							<KebabMenu
								items={kebabItems}
								onAddApplicant={handlers.onAddApplicant}
								onRequestGuarantor={handlers.onRequestGuarantor}
								onRequestBankAccount={handlers.onRequestBankAccount}
								onRequestDocuments={handlers.onRequestDocuments}
								onEditProperty={() =>
									void navigate(`/properties/${propertyId}/edit`)
								}
								onReachOut={handlers.onReachOut}
								onSendUpdateLink={handlers.onSendUpdateLink}
								onWithdraw={handlers.onWithdraw}
								onReinstate={handlers.onReinstate}
								onRegenerateReport={handlers.onRegenerateReport}
								onDeleteApplication={handlers.onDeleteApplication}
							/>
						) : null
					}
					onViewChange={(view) => {
						if (view === 'individual') {
							void navigate(
								preserveReturnTo(
									`/properties/${propertyId}/applications/${application.id}/v2`,
									searchParams,
								),
							)
						}
					}}
				/>

				<CombinedHeader
					members={data.members}
					householdPropertyStep={data.householdPropertyStep}
					familyScore={data.familyScore}
					familyPredictions={familyPredictions}
					guarantorRequests={data.guarantorRequests}
					onRequestGuarantor={
						// Rule 2 carve-out (client 2026-07-21): guarantors can be
						// requested on DECIDED households too — a joining member
						// re-opens it (rule 6), so no decisionLocked gate here.
						can.requestGuarantor ? handlers.onRequestGuarantor : undefined
					}
					// Live invitations count toward the "waiting on N submissions"
					// line — an invited member is expected, just not started.
					pendingInvitees={data.pendingInvitees}
					// Once nobody is waiting, these drive the upgrade offer that
					// replaces the unavailable state in the same slot.
					preScreenApplicants={data.preScreenApplicants}
					onGetReport={
						data.canFullscreen ? handlers.onProceedToFullScreen : undefined
					}
					scoreDisplay={data.scoreDisplay}
					isStudentHousing={isStudentHousing}
					// The failed copy points at the kebab's Generate Report only
					// when that item is actually offered.
					regenerateOffered={familyReportAction != null}
				/>
			</header>

			<main className={cn(DETAIL_CONTAINER, 'mt-6 flex flex-col gap-6')}>
				{reportView === 'processing' && data.application.groupId ? (
					<FamilyProcessingSection groupId={data.application.groupId} />
				) : null}
				{/* No failed-report card here (client 2026-09-07): the header's
				    "Combined Report Unavailable" state names the failure itself, and
				    the kebab keeps the Generate action. The 2026-08-27 failed card
				    with its own CTA is gone. */}
				{reportView === 'report' ? (
					<SummaryTilesView
						rentRange={rentRange}
						savings={savings}
						propertyStep={data.primaryPropertyStep}
						// Same reshuffle as the individual page (client 2026-08-27): the
						// family narrative is the card's first row, tiles the second.
						aiSummary={
							<AiSummaryBody text={familyPredictions?.summary ?? null} />
						}
					/>
				) : null}
				<MemberOverviews
					members={data.members}
					pendingInvitees={data.pendingInvitees}
					// Children ride the household-max property step — the same source
					// the header's "N Children" chip counts.
					householdChildren={data.householdPropertyStep?.children ?? []}
					propertyId={propertyId}
					entryApplicationId={application.id}
					// Rule 2 carve-out (client 2026-07-21): Add Applicant stays
					// available on DECIDED households (all terminal statuses) — a
					// joining member re-opens the household (rule 6). The server's
					// areAllApplicantsDecided 409s were removed to match.
					canAddApplicant={can.addApplicant}
					// NOT decision-locked (2026-07-13): a decided household may still
					// DETACH members (mistake-only for terminal members — the dialog
					// enforces it); removal is record reorganization, not a decision.
					canRemoveMember={can.removeMember}
					canCancelInvite={!decisionLocked && can.cancelInvitation}
					// Child tiles carry the same ✕ as members (client 2026-09-07):
					// removal edits every applicant's submitted household details,
					// gated on the same merge permission as a detach.
					canRemoveChild={can.removeChild}
					onAddApplicant={handlers.onAddApplicant}
					onRemoveMember={handlers.onRemoveMember}
					onRemoveChild={handlers.onRemoveChild}
				/>
				{/* Court Records tile (2026-09-08) gates on OpenRoom exactly like
				    the individual page; per-member summaries are FULL members only. */}
				<CombinedVerificationSummary
					familyPredictions={familyPredictions}
					members={data.members}
					memberVerifications={data.memberVerifications}
					memberCourtRecords={data.memberCourtRecords}
					openroomEnabled={data.openroomEnabled}
					propertyId={propertyId}
					canDownload={can.download || isDemoProperty(application.propertyId)}
				/>
				{familyPredictions ? (
					<RiskFlagPanels
						red={familyPredictions.riskFlags.red}
						yellow={familyPredictions.riskFlags.yellow}
						title="Combined Risk Flags & Alerts"
					/>
				) : null}
				{demoHidesIndividualFlags(application.groupId) ? null : (
					<IndividualFlags
						members={data.members}
						memberFlags={data.memberFlags}
					/>
				)}
				{/* Individual-page parity (client 2026-09-08): the same two closing
				    sections, one accordion row per member, in the individual
				    page's order — Additional Documents 2nd last, Consent last. */}
				<MemberDocuments
					members={data.members}
					memberDocuments={data.memberDocuments}
				/>
				<MemberConsents
					members={data.members}
					memberConsents={data.memberConsents}
				/>
			</main>
		</div>
	)
}
